import { CloudDownloadOutlined, PictureOutlined, PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Flex, Input, Modal, Segmented, Select, Space, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { useListPage } from "@/components/list/useListPage";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { useConnections } from "../channels/hooks";
import { PlatformLabel } from "../channels/PlatformBadge";

export type Product = Schemas["Product"];
export type ProductSummary = Schemas["ProductSummary"];
export type ChannelStatus = Schemas["ProductChannelStatus"];

const statusColor: Record<ChannelStatus, string> = {
  not_published: "default",
  draft: "default",
  in_review: "processing",
  live: "success",
  rejected: "error",
  inactive: "warning",
  deleted: "default",
  failed: "error",
};

export function ChannelStatusTag({ status }: { status: ChannelStatus }) {
  const { t } = useI18n();
  return <Tag color={statusColor[status]}>{t(`prod.status.${status}`)}</Tag>;
}

/** A product picture fetched with the session (pictures are not public). */
export function ProductImage({ productId, imageId, size = 48 }: { productId: string; imageId: string | null | undefined; size?: number }) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!imageId) return;
    let live = true;
    let made: string | undefined;
    void unwrap(api.GET("/v1/products/{productID}/images/{imageID}", { params: { path: { productID: productId, imageID: imageId } }, parseAs: "blob" })).then(
      (b) => {
        made = URL.createObjectURL(b);
        if (live) setUrl(made);
      },
      () => undefined,
    );
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [productId, imageId]);
  const box = { width: size, height: size, borderRadius: 4, background: "var(--ant-color-fill-tertiary)", objectFit: "cover" as const };
  if (!imageId || !url) {
    return (
      <Flex align="center" justify="center" style={box}>
        <PictureOutlined style={{ color: "var(--ant-color-text-quaternary)" }} />
      </Flex>
    );
  }
  return <img src={url} alt="" style={box} />;
}

/** Marketplaces products are published to from Zerpid. */
export const PUBLISH_PLATFORMS: Schemas["ChannelPlatformName"][] = ["tiktok", "tokopedia", "blibli", "lazada"];

/** The stores products are published to from Zerpid (TikTok Shop, Tokopedia, Blibli and Lazada). */
export function usePublishStores() {
  const connections = useConnections();
  return (connections.data?.items ?? []).filter((c) => PUBLISH_PLATFORMS.includes(c.platform));
}

type Show = "active" | "archived";

/**
 * Produk marketplace (Plan §6.15): master products published to the stores.
 * Variants are SKUs of the catalog; stock and orders stay with them.
 */
export function Products() {
  const { t, formatMoney, formatDateTime } = useI18n();
  const { me, can } = useAuth();
  const navigate = useNavigate();
  const tz = me?.workspace?.timezone;
  const page = useListPage();
  const stores = usePublishStores();
  const [show, setShow] = useUrlFilter<Show>("show", "active", ["active", "archived"]);
  const [q, setQ] = useUrlFilter<string>("q", "");
  const [store, setStore] = useUrlFilter<string>("store", "");
  const [channelStatus, setChannelStatus] = useUrlFilter<string>("published", "");
  const [importing, setImporting] = useState(false);
  const filters = {
    status: show,
    ...(q ? { q } : {}),
    ...(store ? { connectionId: store } : {}),
    ...(store && channelStatus ? { channelStatus: channelStatus as ChannelStatus } : {}),
  };
  const list = useQuery({
    queryKey: ["products", "list", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/products", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size={0}>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
            {t("prod.title")}
          </Typography.Title>
          <Typography.Text type="secondary">{t("prod.subtitle")}</Typography.Text>
        </Space>
        {can("catalog.manage") && (
          <Space wrap>
            <Button icon={<CloudDownloadOutlined />} onClick={() => setImporting(true)} disabled={stores.length === 0}>
              {t("prod.import")}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/app/products/new")}>
              {t("prod.add")}
            </Button>
          </Space>
        )}
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <Segmented<Show>
          value={show}
          onChange={setShow}
          options={[
            { value: "active", label: t("prod.show.active") },
            { value: "archived", label: t("prod.show.archived") },
          ]}
        />
        <Input.Search allowClear defaultValue={q} placeholder={t("prod.search")} onSearch={(v) => setQ(v.trim())} style={{ width: 320 }} />
        <Select<string>
          value={store}
          onChange={(v) => (setStore(v), setChannelStatus(""))}
          style={{ width: 220 }}
          options={[{ value: "", label: t("orders.allStores") }, ...stores.map((c) => ({ value: c.id, label: c.name }))]}
        />
        {store && (
          <Select<string>
            value={channelStatus}
            onChange={setChannelStatus}
            style={{ width: 200 }}
            options={[
              { value: "", label: t("prod.filter.anyStatus") },
              ...(["live", "in_review", "rejected", "failed", "draft", "not_published", "inactive"] as const).map((s) => ({
                value: s,
                label: t(`prod.status.${s}`),
              })),
            ]}
          />
        )}
      </Flex>
      <Table<ProductSummary>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.items ?? []}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("prod.empty") }}
        onRow={(x) => ({ onClick: () => navigate(`/app/products/${x.id}`), style: { cursor: "pointer" } })}
        columns={[
          {
            title: t("prod.col.product"),
            render: (_, x) => (
              <Space size={12}>
                <ProductImage productId={x.id} imageId={x.coverId} />
                <Space orientation="vertical" size={0}>
                  <Typography.Text strong>{x.name}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t("prod.variants", { n: x.variantCount })}
                  </Typography.Text>
                </Space>
              </Space>
            ),
          },
          {
            title: t("prod.col.price"),
            width: 200,
            render: (_, x) =>
              x.minPrice == null ? (
                <Typography.Text type="secondary">—</Typography.Text>
              ) : (
                <span className="z-num">
                  {formatMoney(x.minPrice)}
                  {x.maxPrice != null && x.maxPrice !== x.minPrice ? ` – ${formatMoney(x.maxPrice)}` : ""}
                </span>
              ),
          },
          {
            title: t("prod.col.stores"),
            render: (_, x) =>
              x.channels.length === 0 ? (
                <Typography.Text type="secondary">{t("prod.notInStores")}</Typography.Text>
              ) : (
                <Space orientation="vertical" size={2}>
                  {x.channels.map((c) => (
                    <Space key={c.connectionId} size={6}>
                      <PlatformLabel platform={c.platform} />
                      <Typography.Text style={{ fontSize: 12 }}>{c.shopName}</Typography.Text>
                      <ChannelStatusTag status={c.status} />
                    </Space>
                  ))}
                </Space>
              ),
          },
          { title: t("prod.col.updated"), dataIndex: "updatedAt", width: 170, render: (v: string) => formatDateTime(v, tz) },
        ]}
      />
      <ImportModal open={importing} onClose={() => setImporting(false)} />
    </>
  );
}

/** "Impor dari toko": listings of a store become master products. */
function ImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const stores = usePublishStores();
  const [store, setStore] = useState<string>();
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [results, setResults] = useState<{ externalId: string; productId: string | null; error: string }[] | null>(null);
  const connectionID = store ?? stores[0]?.id;
  const importable = useQuery({
    queryKey: ["products", "importable", connectionID, q],
    queryFn: () =>
      unwrap(api.GET("/v1/products/connections/{connectionID}/importable", { params: { path: { connectionID: connectionID! }, query: q ? { q } : {} } })),
    enabled: open && !!connectionID,
  });
  const run = useMutation({
    mutationFn: () =>
      unwrap(api.POST("/v1/products/connections/{connectionID}/import", { params: { path: { connectionID: connectionID! } }, body: { externalIds: picked } })),
    onSuccess: (r) => {
      setResults(r.results);
      setPicked([]);
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      const ok = r.results.filter((x) => x.productId).length;
      if (ok > 0) void message.success(t("prod.imported", { n: ok }));
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const names = Object.fromEntries((importable.data?.items ?? []).map((x) => [x.externalId, x.name]));
  return (
    <Modal
      open={open}
      title={t("prod.import")}
      onCancel={onClose}
      onOk={() => run.mutate()}
      okText={t("prod.importN", { n: picked.length })}
      okButtonProps={{ disabled: picked.length === 0 || picked.length > 20 }}
      cancelText={t("common.close")}
      confirmLoading={run.isPending}
      width={760}
      destroyOnHidden
      afterClose={() => (setResults(null), setPicked([]))}
    >
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Typography.Text type="secondary">{t("prod.importHelp")}</Typography.Text>
        <Flex gap={12} wrap>
          <Select<string>
            value={connectionID}
            onChange={(v) => (setStore(v), setPicked([]))}
            style={{ width: 240 }}
            options={stores.map((c) => ({ value: c.id, label: c.name }))}
          />
          <Input.Search allowClear placeholder={t("prod.search")} onSearch={(v) => setQ(v.trim())} style={{ width: 280 }} />
        </Flex>
        {results && (
          <Alert
            type={results.some((r) => r.error) ? "warning" : "success"}
            showIcon
            title={t("prod.importDone", { ok: results.filter((r) => r.productId).length, failed: results.filter((r) => r.error).length })}
            description={
              <Space orientation="vertical" size={0}>
                {results
                  .filter((r) => r.error)
                  .map((r) => (
                    <Typography.Text key={r.externalId} type="danger">
                      {names[r.externalId] ?? r.externalId}: {r.error}
                    </Typography.Text>
                  ))}
              </Space>
            }
          />
        )}
        <Table
          size="small"
          rowKey="externalId"
          loading={importable.isFetching}
          dataSource={importable.data?.items ?? []}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          rowSelection={{ selectedRowKeys: picked, onChange: (keys) => setPicked(keys as string[]) }}
          locale={{ emptyText: t("prod.importEmpty") }}
          columns={[
            { title: t("prod.col.product"), dataIndex: "name" },
            { title: t("prod.col.variants"), dataIndex: "skuCount", width: 90, align: "right" },
            {
              title: t("prod.col.mapping"),
              width: 170,
              render: (_, x) =>
                x.unmapped > 0 ? <Tag color="warning">{t("prod.unmappedN", { n: x.unmapped })}</Tag> : <Tag color="success">{t("prod.allMapped")}</Tag>,
            },
          ]}
        />
      </Space>
    </Modal>
  );
}
